import { useCallback } from 'react'
import { useSelectionStore, useSelectionStoreApi } from '@editorStores'

const emptyGuids: string[] = []

/**
 * IFC element selection backed by the global selection store (`type: 'ifc'`). Drop-in replacement
 * for the former `controlStore.selectedIfcElementGuids`: reads resolve to `[]` whenever the
 * current selection is not an IFC selection, and writing an empty array clears the selection.
 */
const useIfcSelection = () => {
  // Narrowing inside the selector keeps the result reference-stable across unrelated selection
  // changes, so consumers (e.g. `IFCGlbModel` in the scene) don't re-render on them.
  const selectedIfcElementGuids = useSelectionStore(state =>
    state.selection?.type === 'ifc' ? state.selection.guids : emptyGuids,
  )
  const select = useSelectionStore(state => state.select)

  // `adopt: 'keep'` — the IFC page drives its panels from `ifcMode`, so picking elements must not
  // change the mode (it only falls back to neutral if the current mode cannot hold an IFC selection).
  const setSelectedIfcElementGuids = useCallback(
    (guids: string[]) => {
      if (guids.length) {
        select({ type: 'ifc', guids }, { adopt: 'keep' })
        return
      }
      // Clearing only ever means "no IFC elements picked": a selection of another kind (e.g. a
      // planar wall picked while an async IFC callback resolves) must be left intact — the old
      // `controlStore.selectedIfcElementGuids` was independent of it too.
      if (useSelectionStore.getState().selection?.type === 'ifc') select(null)
    },
    [select],
  )

  return { selectedIfcElementGuids, setSelectedIfcElementGuids }
}

/**
 * Imperative (non-subscribing) accessor for the current IFC selection, for callbacks that need
 * the latest guids without re-registering — the selection-store counterpart of
 * `useControlStoreApi().getState().selectedIfcElementGuids`.
 */
export const useGetIfcSelectionGuids = () => {
  const selectionStoreApi = useSelectionStoreApi()

  return useCallback(() => {
    const { selection } = selectionStoreApi.getState()
    return selection?.type === 'ifc' ? selection.guids : emptyGuids
  }, [selectionStoreApi])
}

export default useIfcSelection
